import * as THREE from 'three'

import type { Bsp } from './bsp'
import { TextureStore, type StoreEntry } from './TextureStore'
import { makeProceduralDustTexture } from './proceduralDustTextures'

export interface SpawnPoint {
  position: THREE.Vector3
  angle: number
}

export interface WaterArea {
  surfaceY: number
  points: { x: number; z: number }[]
}

export type ArmouryWeaponKind =
  | 'ak'
  | 'mp5'
  | 'tmp'
  | 'p90'
  | 'mac10'
  | 'm4a1'
  | 'm3'
  | 'xm1014'
  | 'm249'

export interface ArmouryPickup {
  kind: ArmouryWeaponKind
  position: THREE.Vector3
}

export interface BuiltMap {
  object: THREE.Group
  spawns: SpawnPoint[]
  waterAreas: WaterArea[]
  armoury: ArmouryPickup[]
  materials: THREE.MeshBasicMaterial[]
  lightmap: THREE.DataTexture
  center: THREE.Vector3
  stats: { faces: number; rendered: number; textures: number; missing: number }
}

const ATLAS_WIDTH = 2048
const LUXEL = 16 // one lightmap sample per 16 texels
const PAD = 1
const LIGHTMAP_INTENSITY = 2.18
const CRATE_OVERLAY_OFFSET = 0.75
const ARMOURY_ITEM_TO_WEAPON = new Map<number, ArmouryWeaponKind>([
  [0, 'mp5'],
  [1, 'tmp'],
  [2, 'p90'],
  [3, 'mac10'],
  [4, 'ak'],
  [6, 'm4a1'],
  [11, 'm3'],
  [12, 'xm1014'],
  [13, 'm249'],
])

type DetailKind = 'stone' | 'sand' | 'wood' | 'metal' | 'neutral'

interface DetailProfile {
  kind: DetailKind
  scale: number
  strength: number
  tint: THREE.Color
  tintStrength: number
}

/** GoldSrc (X east, Y north, Z up) → three.js (X right, Y up, Z toward camera). */
function toThree(x: number, y: number, z: number): [number, number, number] {
  return [x, z, -y]
}

interface FaceVertex {
  pos: [number, number, number] // three-space
  texS: number
  texT: number
}

interface FaceWork {
  verts: FaceVertex[]
  entry: StoreEntry
  texName: string
  texW: number
  texH: number
  transparent: boolean
  water: boolean
  waterSurface: boolean
  // lightmap
  hasLight: boolean
  lmMinS: number
  lmMinT: number
  lmW: number
  lmH: number
  lightOffset: number
  rectX: number
  rectY: number
}

/** Build a renderable group (geometry + a packed lightmap atlas) from a BSP. */
export function buildBspMesh(bsp: Bsp, store: TextureStore): BuiltMap {
  const works: FaceWork[] = []
  const usedTextures = new Set<string>()
  let missing = 0

  // ---- Pass 1: collect faces, texture UVs and per-face lightmap extents ----
  for (const face of bsp.faces) {
    if (face.numEdges < 3) continue

    const texInfo = bsp.texInfos[face.texInfo]
    if (!texInfo) continue
    const bspTex = bsp.textures[texInfo.miptex]
    if (!bspTex) continue
    const name = bspTex.name
    if (TextureStore.shouldSkip(name) || TextureStore.isSky(name)) continue
    const texName = name.toLowerCase()
    const water = isWaterTexture(texName)

    const texW = bspTex.width || 64
    const texH = bspTex.height || 64

    const verts: FaceVertex[] = []
    let minS = Infinity
    let minT = Infinity
    let maxS = -Infinity
    let maxT = -Infinity

    for (let e = 0; e < face.numEdges; e++) {
      const surf = bsp.surfEdges[face.firstEdge + e]
      const edge = Math.abs(surf) * 2
      const vi = surf >= 0 ? bsp.edges[edge] : bsp.edges[edge + 1]
      const gx = bsp.vertices[vi * 3]
      const gy = bsp.vertices[vi * 3 + 1]
      const gz = bsp.vertices[vi * 3 + 2]

      const texS =
        gx * texInfo.s[0] + gy * texInfo.s[1] + gz * texInfo.s[2] + texInfo.s[3]
      const texT =
        gx * texInfo.t[0] + gy * texInfo.t[1] + gz * texInfo.t[2] + texInfo.t[3]

      minS = Math.min(minS, texS)
      maxS = Math.max(maxS, texS)
      minT = Math.min(minT, texT)
      maxT = Math.max(maxT, texT)

      verts.push({ pos: toThree(gx, gy, gz), texS, texT })
    }

    const entry = store.get(name)
    if (entry.missing) missing++
    usedTextures.add(name.toLowerCase())
    const waterSurface = water && isHorizontalFace(verts)

    // Lightmap extents (in luxels) — must match hlrad's floor(min)/ceil(max).
    const lmMinS = Math.floor(minS / LUXEL)
    const lmMinT = Math.floor(minT / LUXEL)
    const lmW = Math.ceil(maxS / LUXEL) - lmMinS + 1
    const lmH = Math.ceil(maxT / LUXEL) - lmMinT + 1

    const lightBytes = lmW * lmH * 3
    const hasLight =
      face.lightOffset >= 0 &&
      face.styles[0] !== 255 &&
      lmW > 0 &&
      lmH > 0 &&
      lmW <= 64 &&
      lmH <= 64 &&
      face.lightOffset + lightBytes <= bsp.lighting.length

    works.push({
      verts,
      entry,
      texName,
      texW,
      texH,
      transparent: entry.transparent,
      water,
      waterSurface,
      hasLight,
      lmMinS,
      lmMinT,
      lmW,
      lmH,
      lightOffset: face.lightOffset,
      rectX: 1,
      rectY: 1,
    })
  }

  // ---- Pack lit faces into a lightmap atlas (simple shelf packer) ----
  // Reserve a 2x2 neutral-grey block at the origin for unlit faces.
  let penX = 4
  let penY = 4
  let rowH = 0
  for (const w of works) {
    if (!w.hasLight) continue
    if (penX + w.lmW + PAD > ATLAS_WIDTH) {
      penX = 4
      penY += rowH + PAD
      rowH = 0
    }
    w.rectX = penX
    w.rectY = penY
    penX += w.lmW + PAD
    rowH = Math.max(rowH, w.lmH)
  }
  const atlasHeight = ceilPow2(penY + rowH + PAD)

  const atlasData = new Uint8Array(ATLAS_WIDTH * atlasHeight * 4)
  // Neutral block (value 128 → ×2 intensity ≈ 1.0 fullbright).
  for (let i = 0; i < ATLAS_WIDTH * atlasHeight * 4; i++) atlasData[i] = 255
  for (let y = 0; y < 4; y++) {
    for (let x = 0; x < 4; x++) {
      const o = (y * ATLAS_WIDTH + x) * 4
      atlasData[o] = atlasData[o + 1] = atlasData[o + 2] = 128
      atlasData[o + 3] = 255
    }
  }
  for (const w of works) {
    if (!w.hasLight) continue
    for (let ly = 0; ly < w.lmH; ly++) {
      for (let lx = 0; lx < w.lmW; lx++) {
        const src = w.lightOffset + (ly * w.lmW + lx) * 3
        const dst = ((w.rectY + ly) * ATLAS_WIDTH + (w.rectX + lx)) * 4
        atlasData[dst] = bsp.lighting[src]
        atlasData[dst + 1] = bsp.lighting[src + 1]
        atlasData[dst + 2] = bsp.lighting[src + 2]
        atlasData[dst + 3] = 255
      }
    }
  }

  const lightmap = new THREE.DataTexture(
    atlasData,
    ATLAS_WIDTH,
    atlasHeight,
    THREE.RGBAFormat,
  )
  lightmap.colorSpace = THREE.LinearSRGBColorSpace
  lightmap.magFilter = THREE.LinearFilter
  lightmap.minFilter = THREE.LinearFilter
  lightmap.wrapS = THREE.ClampToEdgeWrapping
  lightmap.wrapT = THREE.ClampToEdgeWrapping
  lightmap.generateMipmaps = false
  lightmap.channel = 1 // sample the geometry's `uv1` attribute
  lightmap.needsUpdate = true

  // ---- Pass 2: build per-texture geometry (positions, diffuse uv, lightmap uv1) ----
  interface Group {
    pos: number[]
    uv: number[]
    uv1: number[]
    entry: StoreEntry
    texName: string
    transparent: boolean
    water: boolean
  }
  const groups = new Map<string, Group>()

  for (const w of works) {
    if (w.water && !w.waterSurface) continue
    let g = groups.get(w.texName)
    if (!g) {
      g = {
        pos: [],
        uv: [],
        uv1: [],
        entry: w.entry,
        texName: w.texName,
        transparent: w.transparent,
        water: w.water,
      }
      groups.set(w.texName, g)
    }

    const lightmapUv = (v: FaceVertex): [number, number] => {
      if (!w.hasLight) return [1 / ATLAS_WIDTH, 1 / atlasHeight]
      const lu = v.texS / LUXEL - w.lmMinS + 0.5
      const lv = v.texT / LUXEL - w.lmMinT + 0.5
      return [(w.rectX + lu) / ATLAS_WIDTH, (w.rectY + lv) / atlasHeight]
    }

    const n = w.verts.length
    for (let k = 1; k < n - 1; k++) {
      for (const v of [w.verts[0], w.verts[k], w.verts[k + 1]]) {
        g.pos.push(v.pos[0], v.pos[1], v.pos[2])
        g.uv.push(v.texS / w.texW, v.texT / w.texH)
        const [lu, lv] = lightmapUv(v)
        g.uv1.push(lu, lv)
      }
    }
  }

  // ---- Assemble meshes ----
  const object = new THREE.Group()
  object.name = 'bsp'
  const materials: THREE.MeshBasicMaterial[] = []

  for (const g of groups.values()) {
    if (g.pos.length === 0) continue
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(g.pos, 3),
    )
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(g.uv, 2))
    geometry.setAttribute('uv1', new THREE.Float32BufferAttribute(g.uv1, 2))
    geometry.computeVertexNormals() // used by the player's collision raycasts
    geometry.computeBoundingSphere()

    const map = g.water
      ? null
      : makeProceduralDustTexture(g.texName) ?? g.entry.texture
    const material = new THREE.MeshBasicMaterial({
      map,
      color: g.water ? 0x48c7ec : 0xffffff,
      lightMap: lightmap,
      lightMapIntensity: LIGHTMAP_INTENSITY,
      side: THREE.DoubleSide,
    })
    if (g.transparent) material.alphaTest = 0.5
    if (g.water) {
      material.transparent = true
      material.opacity = 0.36
      material.depthWrite = false
      material.alphaTest = 0
      material.fog = false
    }
    if (!g.water) applyDetailShader(material, g.texName)
    materials.push(material)

    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = `bsp:${g.texName}`
    mesh.userData.texName = g.texName
    if (g.water) {
      mesh.userData.nonSolid = true
      mesh.userData.waterSurface = true
    }
    object.add(mesh)
  }

  const crateOverlay = buildCrateOverlay(works)
  if (crateOverlay) {
    object.add(crateOverlay.mesh)
    materials.push(crateOverlay.material)
  }

  const waterAreas = buildWaterAreas(works)

  // ---- Spawn points ----
  const spawns: SpawnPoint[] = []
  const armoury: ArmouryPickup[] = []
  for (const ent of bsp.entities) {
    const cls = ent.classname ?? ''
    const [x, y, z] = (ent.origin ?? '0 0 0').split(/\s+/).map(Number)
    const [tx, ty, tz] = toThree(x, y, z)
    if (cls === 'info_player_start' || cls === 'info_player_deathmatch') {
      spawns.push({
        // GoldSrc info_player_* origin is the player hull CENTRE (~36u above the
        // feet); store the feet so the player drops onto the floor the mapper
        // aligned the spawn box to, not 32u into the air.
        position: new THREE.Vector3(tx, ty - 36, tz),
        angle: Number(ent.angle ?? 0),
      })
    } else if (cls === 'armoury_entity') {
      const item = Number(ent.item ?? 0)
      const kind = ARMOURY_ITEM_TO_WEAPON.get(item)
      if (kind) armoury.push({ kind, position: new THREE.Vector3(tx, ty, tz) })
    }
  }

  const center = new THREE.Box3()
    .setFromObject(object)
    .getCenter(new THREE.Vector3())

  return {
    object,
    spawns,
    waterAreas,
    armoury,
    materials,
    lightmap,
    center,
    stats: {
      faces: bsp.faces.length,
      rendered: works.length,
      textures: usedTextures.size,
      missing,
    },
  }
}

function isWaterTexture(name: string): boolean {
  return name.includes('water') || name.startsWith('!')
}

function isHorizontalFace(verts: FaceVertex[]): boolean {
  if (verts.length < 3) return false
  const a = new THREE.Vector3().fromArray(verts[0].pos)
  const b = new THREE.Vector3().fromArray(verts[1].pos)
  const c = new THREE.Vector3().fromArray(verts[2].pos)
  const n = b.sub(a).cross(c.sub(a))
  if (n.lengthSq() < 1e-5) return false
  n.normalize()
  return Math.abs(n.y) > 0.65
}

function buildWaterAreas(works: FaceWork[]): WaterArea[] {
  const areas: WaterArea[] = []
  const a = new THREE.Vector3()
  const b = new THREE.Vector3()
  const c = new THREE.Vector3()
  const ab = new THREE.Vector3()
  const ac = new THREE.Vector3()
  const n = new THREE.Vector3()

  for (const w of works) {
    if (!w.waterSurface || w.verts.length < 3) continue
    a.fromArray(w.verts[0].pos)
    b.fromArray(w.verts[1].pos)
    c.fromArray(w.verts[2].pos)
    ab.subVectors(b, a)
    ac.subVectors(c, a)
    n.crossVectors(ab, ac)
    if (n.lengthSq() < 1e-5) continue
    n.normalize()
    if (Math.abs(n.y) < 0.65) continue

    const surfaceY =
      w.verts.reduce((sum, v) => sum + v.pos[1], 0) / w.verts.length
    areas.push({
      surfaceY,
      points: w.verts.map((v) => ({ x: v.pos[0], z: v.pos[2] })),
    })
  }

  return areas.sort((a, b) => polygonAreaXZ(b.points) - polygonAreaXZ(a.points))
}

function polygonAreaXZ(points: { x: number; z: number }[]): number {
  let sum = 0
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    sum += points[j].x * points[i].z - points[i].x * points[j].z
  }
  return Math.abs(sum) * 0.5
}

function ceilPow2(n: number): number {
  let p = 1
  while (p < n) p <<= 1
  return p
}

function isCrateTexture(name: string): boolean {
  return name.includes('crte') || name.includes('crt')
}

function buildCrateOverlay(
  works: FaceWork[],
): { mesh: THREE.Mesh; material: THREE.MeshBasicMaterial } | null {
  const pos: number[] = []
  const color: number[] = []
  const faceNormal = new THREE.Vector3()
  const p0 = new THREE.Vector3()
  const p1 = new THREE.Vector3()
  const p2 = new THREE.Vector3()
  const p3 = new THREE.Vector3()
  const edgeA = new THREE.Vector3()
  const edgeB = new THREE.Vector3()

  const pushPanel = (
    points: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3],
    normal: THREE.Vector3,
    u0: number,
    v0: number,
    u1: number,
    v1: number,
    tint: [number, number, number],
  ): void => {
    const a = bilerp(points, u0, v0, normal)
    const b = bilerp(points, u1, v0, normal)
    const c = bilerp(points, u1, v1, normal)
    const d = bilerp(points, u0, v1, normal)
    for (const v of [a, b, c, a, c, d]) {
      pos.push(v.x, v.y, v.z)
      color.push(...tint)
    }
  }

  for (const w of works) {
    if (!isCrateTexture(w.texName) || w.verts.length !== 4) continue

    p0.fromArray(w.verts[0].pos)
    p1.fromArray(w.verts[1].pos)
    p2.fromArray(w.verts[2].pos)
    p3.fromArray(w.verts[3].pos)
    edgeA.subVectors(p1, p0)
    edgeB.subVectors(p2, p1)
    faceNormal.crossVectors(edgeA, edgeB)
    if (faceNormal.lengthSq() < 1e-4) continue
    faceNormal.normalize()

    const width = edgeA.length()
    const height = edgeB.length()
    if (width < 18 || height < 18) continue

    const points: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3] =
      [p0.clone(), p1.clone(), p2.clone(), p3.clone()]
    const frame = Math.min(0.17, Math.max(0.08, 8 / Math.min(width, height)))
    const groove = Math.min(0.018, Math.max(0.008, 1.5 / width))
    const uIsVertical = Math.abs(edgeA.y) > Math.abs(edgeB.y)
    const frameColor: [number, number, number] = [0.12, 0.08, 0.05]
    const highColor: [number, number, number] = [0.22, 0.16, 0.1]
    const darkColor: [number, number, number] = [0.035, 0.025, 0.018]

    for (const side of [1, -1]) {
      const n = faceNormal.clone().multiplyScalar(side * CRATE_OVERLAY_OFFSET)
      pushPanel(points, n, 0, 0, 1, frame, highColor)
      pushPanel(points, n, 0, 1 - frame, 1, 1, darkColor)
      pushPanel(points, n, 0, frame, frame, 1 - frame, frameColor)
      pushPanel(points, n, 1 - frame, frame, 1, 1 - frame, darkColor)

      for (const seam of [0.32, 0.5, 0.68]) {
        if (uIsVertical) {
          pushPanel(
            points,
            n,
            frame * 1.2,
            seam - groove,
            1 - frame * 1.2,
            seam + groove,
            darkColor,
          )
        } else {
          pushPanel(
            points,
            n,
            seam - groove,
            frame * 1.2,
            seam + groove,
            1 - frame * 1.2,
            darkColor,
          )
        }
      }
    }
  }

  if (pos.length === 0) return null

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(color, 3))
  geometry.computeVertexNormals()
  geometry.computeBoundingSphere()

  const material = new THREE.MeshBasicMaterial({
    vertexColors: true,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.72,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  })
  const mesh = new THREE.Mesh(geometry, material)
  mesh.name = 'bsp:crate-procedural-overlays'
  mesh.userData.nonSolid = true
  mesh.renderOrder = 2
  return { mesh, material }
}

function bilerp(
  points: [THREE.Vector3, THREE.Vector3, THREE.Vector3, THREE.Vector3],
  u: number,
  v: number,
  offset: THREE.Vector3,
): THREE.Vector3 {
  const [p0, p1, p2, p3] = points
  return new THREE.Vector3(
    p0.x * (1 - u) * (1 - v) +
      p1.x * u * (1 - v) +
      p2.x * u * v +
      p3.x * (1 - u) * v,
    p0.y * (1 - u) * (1 - v) +
      p1.y * u * (1 - v) +
      p2.y * u * v +
      p3.y * (1 - u) * v,
    p0.z * (1 - u) * (1 - v) +
      p1.z * u * (1 - v) +
      p2.z * u * v +
      p3.z * (1 - u) * v,
  ).add(offset)
}

function detailForTexture(name: string): DetailProfile | null {
  if (
    name === 'black' ||
    name === 'white' ||
    name === 'generic011' ||
    name.includes('lgt')
  ) {
    return null
  }

  if (
    name.includes('crte') ||
    name.includes('crt') ||
    name.includes('door') ||
    name.includes('wndw')
  ) {
    return {
      kind: 'wood',
      scale: 74,
      strength: 0.18,
      tint: new THREE.Color(0x9a7651),
      tintStrength: 0.1,
    }
  }

  if (name.includes('road') || /^-?\d?sand$/i.test(name)) {
    return {
      kind: 'sand',
      scale: 92,
      strength: 0.16,
      tint: new THREE.Color(0xc8a66f),
      tintStrength: 0.08,
    }
  }

  if (name.includes('metal') || name.includes('mltry')) {
    return {
      kind: 'metal',
      scale: 68,
      strength: 0.12,
      tint: new THREE.Color(0x7f8978),
      tintStrength: 0.07,
    }
  }

  if (
    name.includes('wall') ||
    name.includes('crete') ||
    name.includes('rock') ||
    name.includes('trim')
  ) {
    return {
      kind: 'stone',
      scale: 86,
      strength: 0.15,
      tint: new THREE.Color(0xbda17a),
      tintStrength: 0.09,
    }
  }

  return {
    kind: 'neutral',
    scale: 72,
    strength: 0.1,
    tint: new THREE.Color(0xbba98d),
    tintStrength: 0.04,
  }
}

function applyDetailShader(
  material: THREE.MeshBasicMaterial,
  texName: string,
): void {
  const profile = detailForTexture(texName)
  if (!profile) return

  material.onBeforeCompile = (shader) => {
    shader.uniforms.csDetailScale = { value: profile.scale }
    shader.uniforms.csDetailStrength = { value: profile.strength }
    shader.uniforms.csDetailTint = { value: profile.tint }
    shader.uniforms.csDetailTintStrength = { value: profile.tintStrength }

    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_pars_fragment>',
      /* glsl */ `
        #include <map_pars_fragment>

        uniform float csDetailScale;
        uniform float csDetailStrength;
        uniform vec3 csDetailTint;
        uniform float csDetailTintStrength;

        float csHash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        float csNoise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          f = f * f * (3.0 - 2.0 * f);

          float a = csHash(i);
          float b = csHash(i + vec2(1.0, 0.0));
          float c = csHash(i + vec2(0.0, 1.0));
          float d = csHash(i + vec2(1.0, 1.0));

          return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
        }

        float csFbm(vec2 p) {
          float v = 0.0;
          float a = 0.5;
          mat2 r = mat2(1.61, 1.08, -1.08, 1.61);

          for (int i = 0; i < 4; i++) {
            v += csNoise(p) * a;
            p = r * p + 13.7;
            a *= 0.5;
          }

          return v;
        }
      `,
    )

    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <map_fragment>',
      /* glsl */ `
        #include <map_fragment>

        #ifdef USE_MAP
          float coarse = csFbm(vMapUv * csDetailScale);
          float fine = csNoise(vMapUv * csDetailScale * 3.8);
          float grit = ((coarse - 0.5) * 1.25 + (fine - 0.5) * 0.45) * csDetailStrength;
          vec3 tinted = diffuseColor.rgb * mix(vec3(1.0), csDetailTint, csDetailTintStrength);
          diffuseColor.rgb = max(tinted * (1.0 + grit), vec3(0.0));
        #endif
      `,
    )
  }
  material.customProgramCacheKey = () => `cs-detail-${profile.kind}`
}
